import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { environment } from '@environment/environment';
import type { RoleFiltersDTO, RoleListResponseDTO, RoleResponseDTO } from '@libs/dtos/role/role.dto';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { fetchAllPages } from '../../utils/fetch-all-pages';

/** Role list page; the SSR/error fallback only carries data and total */
export type RoleListPage = Pick<RoleListResponseDTO, 'data' | 'total'> & Partial<RoleListResponseDTO>;

@Injectable({
	providedIn: 'root',
})
export class RoleService {
	private baseUrl = `${environment.pathBackend}/roles`;
	private platformId = inject(PLATFORM_ID);
	private allRoles$: Observable<RoleResponseDTO[]> | null = null;

	constructor(private http: HttpClient) {}

	/**
	 * Checks if we're running on server side
	 */
	private isServer(): boolean {
		return isPlatformServer(this.platformId);
	}

	/**
	 * Get all roles with pagination
	 */
	getAllRoles(params: Partial<RoleFiltersDTO>): Observable<RoleListPage> {
		// If we're in SSR, return empty data
		if (this.isServer()) {
			return of({ data: [], total: 0 });
		}

		let httpParams = new HttpParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined) {
				httpParams = httpParams.set(key, value);
			}
		}

		// No catchError: callers show the load error (a failed request is not an empty list)
		return this.http.get<RoleListResponseDTO>(this.baseUrl, { params: httpParams });
	}

	/**
	 * Every role sorted by name, requested once and shared by the screens that need options
	 * (users filter, user form). The frontend never edits roles; a failed load is retried next time.
	 */
	getRoleOptions(): Observable<RoleResponseDTO[]> {
		if (this.isServer()) return of([]);
		if (!this.allRoles$) {
			// Every page (the API serves at most 100 per page)
			this.allRoles$ = fetchAllPages((paging) =>
				this.http.get<RoleListResponseDTO>(this.baseUrl, {
					params: { ...paging, sort_by: 'name', sort_order: 'asc' },
				}),
			).pipe(
				map((response) => response?.data ?? []),
				tap({
					error: () => {
						this.allRoles$ = null;
					},
				}),
				shareReplay(1),
			);
		}
		return this.allRoles$;
	}

	/** Drops the shared role list (e.g. after a role changes) */
	invalidateRoles(): void {
		this.allRoles$ = null;
	}

	/**
	 * Get role by ID
	 */
	getRoleById(id: number): Observable<RoleResponseDTO | null> {
		if (this.isServer()) {
			return of(null);
		}

		return this.http.get<RoleResponseDTO>(`${this.baseUrl}/${id}`).pipe(
			catchError((error) => {
				throw error;
			}),
		);
	}
}
